import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';

/**
 * Canonical assignable channel roster via listChannelMemberships.
 *
 * Migrated to listUsers / listChannelMembershipsV2 in the channel-members epic:
 * - Member count: useChannelMemberCount
 * - Members modal: useChannelMembersList + useChannelMembershipsForUsers
 * - Add members: listUsers with notMemberOfChannelIds
 *
 * Intentionally kept here for @mentions, person filters, seen-by, and person fields.
 */
interface Props {
  channelId?: number;
}

interface ChannelUsers {
  users: API.CollectionViewUserFragment[];
  memberships: API.AssignableChannelMembershipFragment[];
  loading: boolean;
}

export function useChannelUsers({ channelId }: Props): ChannelUsers {
  const { data, loading } = useQuery<
    API.ListChannelMembershipsQuery,
    API.ListChannelMembershipsQueryVariables
  >(Queries.channel.listChannelMemberships, {
    variables: {
      input: {
        channelId: channelId ?? -1,
        onlyAssignableUsers: true,
      },
    },
    skip: isNil(channelId),
  });

  return useMemo(() => {
    const memberships = (data?.listChannelMemberships?.filter(notNil) ?? []).filter(m =>
      notNil(m.user),
    );
    return {
      users: memberships.map(({ user }) => user),
      memberships,
      loading,
    };
  }, [data?.listChannelMemberships, loading]);
}
